import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { useDispatch, useSelector } from 'react-redux'
import Dropdown from '../../../components/Dropdown/Dropdown'
import {
  loadAssignableCoordinators,
  selectAssignableUsers
} from '../../../features/workflow/workflowSlice'
import { getTaskTemplatesReq } from '../../../features/tasks/tasks'

// Task-creating action keys that accept a template / due-offset / title config
// (e.g. the payment-failure follow-up: PAYMENT_FAILED -> CREATE_TASK, NSF -> NSF_CALL_TASK).
const TASK_KEYS = ['CREATE_TASK', 'NSF_CALL_TASK', 'REVIEW_CLIENT_TASK']
const SUPPORTED_KEYS = [...TASK_KEYS, 'ASSIGN_CONTACT']

const TaskActionConfig = ({ config, onChange, actionKey }) => {
  const dispatch = useDispatch()
  const assignableUsers = useSelector(selectAssignableUsers)
  const [templates, setTemplates] = useState([])

  const isTaskAction = TASK_KEYS.includes(actionKey)

  const [localConfig, setLocalConfig] = useState({
    assigneeId: config?.assigneeId || null,
    taskTemplate: config?.taskTemplate || '',
    daysOffset: config?.daysOffset ?? '',
    customTitle: config?.customTitle || '',
  })

  useEffect(() => {
    setLocalConfig((prev) => ({
      assigneeId: config?.assigneeId ?? prev.assigneeId ?? null,
      taskTemplate: config?.taskTemplate ?? prev.taskTemplate ?? '',
      daysOffset: config?.daysOffset ?? prev.daysOffset ?? '',
      customTitle: config?.customTitle ?? prev.customTitle ?? '',
    }))
  }, [config])

  useEffect(() => {
    if (SUPPORTED_KEYS.includes(actionKey)) dispatch(loadAssignableCoordinators())
    if (isTaskAction) {
      dispatch(getTaskTemplatesReq()).then((res) => {
        const list = res?.payload?.data || res?.payload || []
        setTemplates(Array.isArray(list) ? list : [])
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, actionKey])

  if (!SUPPORTED_KEYS.includes(actionKey)) return null

  // Emit a clean config: omit empty optional fields so the backend uses its defaults.
  const update = (patch) => {
    const next = { ...localConfig, ...patch }
    setLocalConfig(next)
    const outgoing = { assigneeType: 'user', assigneeId: next.assigneeId || null }
    if (isTaskAction) {
      if (next.taskTemplate) outgoing.taskTemplate = next.taskTemplate
      if (next.daysOffset !== '' && next.daysOffset != null) outgoing.daysOffset = Number(next.daysOffset)
      if (next.customTitle) outgoing.customTitle = next.customTitle
    }
    onChange(outgoing)
  }

  const userOptions = (assignableUsers || []).map((user) => ({
    value: user._id,
    label: `${user.firstName} ${user.lastName}${
      user.role === 'customer' ? ' (Customer)' : user.subRoleLabel ? ` (${user.subRoleLabel})` : ''
    }`,
  }))

  const templateOptions = (templates || []).map((t) => ({
    value: t.value || t._id,
    label: t.label || t.name || t.value,
  }))

  return (
    <div className="flex flex-col gap-4 p-4 bg-surface rounded-lg border border-divider mt-4">
      <h4 className="text-lg font-semibold text-text-primary">
        {actionKey === 'ASSIGN_CONTACT' ? 'Contact Assignment Configuration' : 'Task Configuration'}
      </h4>

      {isTaskAction && (
        <>
          <div>
            <Dropdown
              label="Task template"
              placeholder="Select a task template..."
              value={localConfig.taskTemplate || ''}
              setValue={(v) => update({ taskTemplate: v })}
              menuItems={templateOptions}
              className="w-full"
            />
            <p className="text-xs text-text-secondary mt-1 ml-4">
              The task created when this fires — title/description come from the template unless overridden below.
            </p>
          </div>
          <div className="flex flex-wrap gap-4">
            <div className="w-40">
              <label className="text-sm text-text-primary">Due in (days)</label>
              <input
                type="number"
                min="0"
                value={localConfig.daysOffset}
                onChange={(e) => update({ daysOffset: e.target.value === '' ? '' : Number(e.target.value) })}
                placeholder="e.g. 3"
                className="w-full mt-1 rounded-lg border border-divider bg-paper px-3 py-2 text-sm text-text-primary"
              />
            </div>
            <div className="flex-1 min-w-[200px]">
              <label className="text-sm text-text-primary">Custom title (optional)</label>
              <input
                type="text"
                value={localConfig.customTitle}
                onChange={(e) => update({ customTitle: e.target.value })}
                placeholder="Overrides the template title"
                className="w-full mt-1 rounded-lg border border-divider bg-paper px-3 py-2 text-sm text-text-primary"
              />
            </div>
          </div>
        </>
      )}

      <div>
        <Dropdown
          label="Assign to"
          placeholder={isTaskAction ? 'Default: whoever triggered the failure' : 'Select user...'}
          value={localConfig.assigneeId || ''}
          setValue={(userId) => update({ assigneeId: userId || null })}
          menuItems={userOptions}
          className="w-full"
        />
        <p className="text-xs text-text-secondary mt-1 ml-4">
          {actionKey === 'ASSIGN_CONTACT'
            ? "User added to the contact's assignees when the trigger fires."
            : 'Leave empty to assign to whoever triggered the payment failure.'}
        </p>
      </div>

      <div className="bg-primary-main/10 border border-primary-main/20 rounded p-3 mt-2">
        <p className="text-xs text-text-secondary">
          <strong>How it works:</strong>{' '}
          {actionKey === 'ASSIGN_CONTACT'
            ? "When the trigger fires, the selected user is added to the contact's assignees."
            : 'When the trigger fires (e.g. a payment fails), this task is created for the client and assigned as above.'}
        </p>
      </div>
    </div>
  )
}

TaskActionConfig.propTypes = {
  config: PropTypes.shape({
    assigneeType: PropTypes.string,
    assigneeId: PropTypes.string,
    taskTemplate: PropTypes.string,
    daysOffset: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    customTitle: PropTypes.string,
  }),
  onChange: PropTypes.func.isRequired,
  actionKey: PropTypes.string,
}

TaskActionConfig.defaultProps = {
  config: { assigneeType: 'user', assigneeId: null },
}

export default TaskActionConfig
